/**
 * WEB-903 (option B): DatoCMS editors sometimes author an internal link as a
 * full absolute URL (e.g. `https://zoe.com/daily30`). Our CTA blocks treat any
 * `http(s)`/`//` href as external and do a `window.location.href` HARD reload —
 * which wipes the in-memory `lead_id` even though it's really an internal hop.
 *
 * This normalises a first-party absolute URL back to a relative path so the
 * navigation stays a client-side (SPA) transition and the in-memory id survives.
 * Third-party URLs are returned untouched (we must never relativise — or carry
 * the id onto — a different site).
 *
 * First-party hosts are derived from env (inlined into the client bundle), so
 * the result is identical on server and client — no hydration mismatch.
 */

let firstPartyHosts: Set<string> | null = null

export function getFirstPartyHosts(): Set<string> {
  if (firstPartyHosts) return firstPartyHosts

  // NEXT_PUBLIC_SITE_URL is inlined on both server and client, so the resolved
  // set is identical in each (no hydration mismatch).
  const candidates = [
    process.env.NEXT_PUBLIC_SITE_URL,
    'https://zoe.com',
    'https://www.zoe.com',
    'https://joinzoe.com',
    'https://www.joinzoe.com',
  ]
  const hosts = new Set<string>()
  for (const candidate of candidates) {
    if (!candidate) continue
    try {
      hosts.add(new URL(candidate).host)
    } catch {
      // ignore malformed values
    }
  }
  firstPartyHosts = hosts
  return hosts
}

/**
 * Returns a relative `path?query#hash` when `url` is an absolute first-party
 * URL; otherwise returns `url` unchanged (relative paths, hashes and
 * third-party URLs all pass through).
 */
export function toFirstPartyRelativeUrl(url: string): string {
  if (!url) return url
  const isProtocolRelative = url.startsWith('//')
  if (!isProtocolRelative && !/^https?:\/\//i.test(url)) return url
  try {
    const parsed = new URL(isProtocolRelative ? `https:${url}` : url)
    if (getFirstPartyHosts().has(parsed.host)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    }
  } catch {
    // malformed URL — leave it as-is
  }
  return url
}

/**
 * True when `host` is one of ours for the purpose of CARRYING the lead_id.
 *
 * Deliberately wider than `getFirstPartyHosts`: it also accepts the origin the page is served from,
 * which is first-party by definition. Without it, a deployment on any other domain — stage, a Vercel
 * preview, a branch deploy — drops the id from every absolute self-referencing URL it builds, the
 * Stripe return_url included, and attribution dies at the redirect back (WEB-1262).
 *
 * Kept out of `getFirstPartyHosts` because that set also drives `toFirstPartyRelativeUrl`, which
 * runs during render on both server and client and must resolve identically in each. This is only
 * reached from `appendLeadId`, which no-ops until the provider has captured an id in an effect, so
 * it never runs during the first render and cannot desync it.
 */
export function isFirstPartyHostForLeadId(host: string): boolean {
  if (getFirstPartyHosts().has(host)) return true
  return typeof window !== 'undefined' && host === window.location.host
}
